import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, shopApi, getShopSession } from "@/lib/api";
import { useShopIdleLogout } from "@/hooks/useShopIdleLogout";
import { ShopHeader } from "@/components/ShopHeader";
import { ReportPrintHeader } from "@/components/ReportPrintHeader";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, TrendingUp, Wallet, LineChart as LineIcon, Award, ChevronLeft, Printer, Trophy, X,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, ComposedChart, LabelList, PieChart, Pie, Cell,
} from "recharts";

// Palette for pie charts — chosen to echo the shop reports colour family while
// staying distinguishable at pie-slice sizes.
const PIE_COLORS = [
  "#065f46", "#a16207", "#1d4ed8", "#9d174d", "#b45309",
  "#047857", "#7c2d12", "#4338ca", "#be123c", "#0f766e",
  "#92400e", "#365314",
];

const RANGES = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

function fmtTHBn(n) { return `฿${Math.round(Number(n || 0)).toLocaleString("en-US")}`; }
function fmtTHB(n) { return `฿${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

function KPITile({ icon: Icon, label, value, testId, highlight }) {
  return (
    <Card className={`border-stone-200/80 ${highlight ? "bg-emerald-50/60 border-emerald-200" : ""}`} data-testid={testId}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded flex items-center justify-center ${highlight ? "bg-emerald-800 text-amber-50" : "bg-stone-100 text-stone-700"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="caption-label truncate">{label}</div>
          <div className={`font-mono tabular text-2xl mt-0.5 ${highlight ? "text-emerald-900" : "text-stone-900"}`}>{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ShopKPI() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [shop, setShop] = useState(null);
  const [window, setWindow] = useState("daily");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const customActive = Boolean(customStart && customEnd);

  useShopIdleLogout(slug, t("shop.idle_signout"));

  useEffect(() => {
    const sess = getShopSession(slug);
    if (!sess?.token) { nav(`/${slug}`); return; }
    // KPIs are for owner/manager (and impersonating super admin, who carries an owner token).
    if (!["owner", "manager"].includes(sess.role)) { nav(`/${slug}`); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/shops/by-slug/${slug}`);
        setShop(r.data);
      } catch (e) { /* header renders without shop data */ }
    })();
  }, [slug, nav]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = customActive
          ? { window, start: customStart, end: customEnd }
          : { window };
        const r = await shopApi(slug).get(`/shops/${slug}/reports/kpi`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load KPIs");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug, window, customActive, customStart, customEnd]);

  const totals = data?.totals || {};
  const buckets = data?.buckets || [];
  const hourly = data?.hourly || [];
  const allServices = (data?.all_services || []).map(s => ({
    ...s,
    display_name: (lang === "th" && s.name_th) ? s.name_th : s.name,
  }));
  const byTherapist = data?.by_therapist || [];
  const fmtBaht = (v) => `฿${Math.round(Number(v || 0)).toLocaleString("en-US")}`;
  const fmtHours = (v) => `${Number(v || 0).toFixed(1)}h`;
  const incomeByTherapist = byTherapist
    .filter(s => Number(s.income || 0) > 0)
    .map(s => ({ name: s.name, value: Math.round(Number(s.income || 0)), delta_pct: s.income_delta_pct, display: fmtBaht(s.income) }));
  const hoursByTherapist = byTherapist
    .filter(s => Number(s.hours || 0) > 0)
    .map(s => ({ name: s.name, value: Number(s.hours || 0), delta_pct: s.hours_delta_pct, display: fmtHours(s.hours) }));
  const payByTherapist = byTherapist
    .filter(s => Number(s.pay || 0) > 0)
    .map(s => ({ name: s.name, value: Math.round(Number(s.pay || 0)), delta_pct: s.pay_delta_pct, display: fmtBaht(s.pay) }));
  const compareLabel = window === "daily" ? t("kpi.vs_dod")
    : window === "weekly" ? t("kpi.vs_wow")
    : t("kpi.vs_mom");
  // Custom pie label — one anchored line per slice: "Name 47% ↑12%" (or em-dash
  // when there is no comparable prior-period value). Rendered as SVG inside
  // Recharts' Pie so it tracks slice midAngle.
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name, payload }) => {
    const RAD = Math.PI / 180;
    const r = outerRadius + 18;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    const anchor = x > cx ? "start" : "end";
    const delta = payload?.delta_pct;
    const hasDelta = delta !== null && delta !== undefined && Number.isFinite(delta);
    const up = hasDelta && delta >= 0;
    const deltaText = hasDelta ? `${up ? "▲" : "▼"} ${Math.abs(delta).toFixed(1)}%` : "— new";
    const deltaFill = hasDelta ? (up ? "#065f46" : "#b91c1c") : "#78716c";
    const shareText = `${(percent * 100).toFixed(0)}%`;
    const valueText = payload?.display ? ` · ${payload.display}` : "";
    return (
      <g style={{ fontSize: 11 }}>
        <text x={x} y={y - 10} textAnchor={anchor} fill="#44403c" fontWeight="600">
          {`${name}${valueText}`}
        </text>
        <text x={x} y={y + 4} textAnchor={anchor} fill="#57534e">
          {shareText}
        </text>
        <text x={x} y={y + 16} textAnchor={anchor} fill={deltaFill} fontWeight="600">
          {deltaText}
        </text>
      </g>
    );
  };
  const useHourly = customActive ? (customStart === customEnd) : (window === "daily");
  const traceLabel = useHourly ? "Hour" : "Date";
  const showHourly = !useHourly;

  const handlePrint = () => {
    if (!shop?.logo_path) {
      toast.error(t("reports.logo_required"));
      return;
    }
    globalThis.print();
  };

  return (
    <div className="min-h-screen shop-bg">
      <ShopHeader shop={shop} showSignOut />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <button
          onClick={() => nav(`/${slug}`)}
          className="caption-label flex items-center gap-1 mb-2 text-stone-500 hover:text-stone-800 print:hidden"
          data-testid="kpi-back-link"
        >
          <ChevronLeft className="w-3 h-3" /> back
        </button>
        <ReportPrintHeader
          shop={shop}
          title={`${t("kpi.title")} — ${t(`kpi.range.${window}`)}`}
          range={data ? `${data.start} → ${data.end}` : ""}
        />
        {!shop?.logo_path && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 print:hidden" data-testid="kpi-logo-missing-banner">
            {t("reports.logo_required")}
          </div>
        )}
        {data?.top_performer_month && (
          <div
            className="relative mb-6 overflow-hidden rounded-lg border border-emerald-800/40 bg-gradient-to-r from-emerald-900 via-emerald-800 to-amber-700 text-amber-50 px-5 py-4 shadow-sm w-full lg:w-1/3"
            data-testid="kpi-top-performer-ribbon"
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-amber-400/20 blur-xl" aria-hidden />
            <div className="relative flex items-center gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-full bg-amber-400/90 text-emerald-950 flex items-center justify-center shrink-0 shadow-md">
                <Trophy className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="caption-label tracking-widest text-amber-200/90">{t("kpi.top_performer_month")}</div>
                <div className="font-serif text-2xl leading-tight truncate" data-testid="kpi-top-performer-name">
                  {data.top_performer_month.name}
                </div>
                <div className="text-sm text-amber-100/85 font-mono tabular mt-0.5">
                  {t("kpi.top_performer_stats")
                    .replace("{income}", fmtTHBn(data.top_performer_month.income))
                    .replace("{customers}", data.top_performer_month.customers)
                    .replace("{hours}", Number(data.top_performer_month.hours || 0).toFixed(1))}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3 print:hidden">
          <h1 className="font-serif text-4xl text-stone-900">{t("kpi.title")}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="inline-flex bg-stone-100 rounded-full p-1"
              role="tablist"
              data-testid="kpi-range-tabs"
            >
              {RANGES.map(r => (
                <button
                  key={r.key}
                  onClick={() => setWindow(r.key)}
                  data-testid={`kpi-range-${r.key}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                    !customActive && window === r.key
                      ? "bg-emerald-800 text-amber-50 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  {t(`kpi.range.${r.key}`)}
                </button>
              ))}
            </div>
            <div
              className="inline-flex items-center gap-1.5 bg-stone-100 rounded-full px-2 py-1"
              data-testid="kpi-date-filter"
            >
              <Input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-7 w-[140px] bg-white rounded-full border-stone-200 text-sm px-3"
                data-testid="kpi-date-start"
                aria-label={t("kpi.date_from")}
              />
              <span className="text-stone-500 text-xs">→</span>
              <Input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-7 w-[140px] bg-white rounded-full border-stone-200 text-sm px-3"
                data-testid="kpi-date-end"
                aria-label={t("kpi.date_to")}
              />
              {customActive && (
                <button
                  type="button"
                  onClick={() => { setCustomStart(""); setCustomEnd(""); }}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-stone-500 hover:bg-stone-200 hover:text-stone-800"
                  aria-label={t("kpi.date_clear")}
                  data-testid="kpi-date-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="kpi-print">
              <Printer className="w-3.5 h-3.5 mr-1" /> {t("common.print")}
            </Button>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8" data-testid="kpi-tiles">
          <KPITile icon={Users} label={t("kpi.customers")} value={loading ? "…" : (totals.customers ?? 0)} testId="kpi-tile-customers" />
          <KPITile icon={Wallet} label={t("kpi.income")} value={loading ? "…" : fmtTHBn(totals.income)} testId="kpi-tile-income" />
          <KPITile icon={TrendingUp} label={t("kpi.profit")} value={loading ? "…" : fmtTHBn(totals.profit)} testId="kpi-tile-profit" highlight />
          <KPITile icon={LineIcon} label={t("kpi.income_per_customer")} value={loading ? "…" : fmtTHB(totals.income_per_customer)} testId="kpi-tile-inc-per-cust" />
          <KPITile icon={LineIcon} label={t("kpi.profit_per_customer")} value={loading ? "…" : fmtTHB(totals.profit_per_customer)} testId="kpi-tile-prof-per-cust" />
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-stone-200/80" data-testid="kpi-chart-customers">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.customers_by")} {traceLabel.toLowerCase()}</h2>
                <span className="caption-label">{data?.start} → {data?.end}</span>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={buckets} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#78716c" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#78716c" />
                    <Tooltip formatter={(v) => v} />
                    <Bar dataKey="customers" name={t("kpi.customers")} fill="#065f46" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200/80" data-testid="kpi-chart-income">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.income")}</h2>
                <span className="caption-label">{traceLabel.toLowerCase()}</span>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={buckets} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#78716c" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#78716c" tickFormatter={(v) => `฿${Math.round(v / 1000)}k`} />
                    <Tooltip formatter={(v) => `฿${Math.round(v).toLocaleString("en-US")}`} />
                    <Bar dataKey="income" name={t("kpi.income")} fill="#a16207" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Per-customer trend charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-stone-200/80" data-testid="kpi-chart-income-per-customer">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.income_per_customer")}</h2>
                <span className="caption-label">{traceLabel.toLowerCase()}</span>
              </div>
              <div className="h-56">
                <ResponsiveContainer>
                  <LineChart data={buckets} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#78716c" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#78716c" tickFormatter={(v) => `฿${Math.round(v)}`} />
                    <Tooltip formatter={(v) => `฿${Math.round(v).toLocaleString("en-US")}`} />
                    <Line type="monotone" dataKey="income_per_customer" name={t("kpi.income_per_customer")} stroke="#a16207" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200/80" data-testid="kpi-chart-profit-per-customer">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.profit_per_customer")}</h2>
                <span className="caption-label">{traceLabel.toLowerCase()}</span>
              </div>
              <div className="h-56">
                <ResponsiveContainer>
                  <LineChart data={buckets} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#78716c" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#78716c" tickFormatter={(v) => `฿${Math.round(v)}`} />
                    <Tooltip formatter={(v) => `฿${Math.round(v).toLocaleString("en-US")}`} />
                    <Line type="monotone" dataKey="profit_per_customer" name={t("kpi.profit_per_customer")} stroke="#065f46" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customers by hour of day — only meaningful on weekly + monthly */}
        {showHourly && (
          <Card className="border-stone-200/80 mb-8" data-testid="kpi-chart-hourly">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.customers_by_hour")}</h2>
                <span className="caption-label">aggregated across {data?.start} → {data?.end}</span>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={hourly} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#78716c" interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#78716c" />
                    <Tooltip formatter={(v) => v} />
                    <Bar dataKey="customers" name={t("kpi.customers")} fill="#a16207" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Therapist pie charts */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card className="border-stone-200/80" data-testid="kpi-pie-income-therapist">
            <CardContent className="p-5">
              <div className="flex items-end justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.income_by_therapist")}</h2>
                <span className="caption-label text-stone-500">{compareLabel}</span>
              </div>
              {incomeByTherapist.length === 0 ? (
                <div className="py-8 text-center text-stone-500 text-sm">{t("reports.no_data")}</div>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer>
                    <PieChart margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
                      <Pie
                        data={incomeByTherapist}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius="65%"
                        label={renderPieLabel}
                        labelLine={false}
                      >
                        {incomeByTherapist.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `฿${Number(v).toLocaleString("en-US")}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-stone-200/80" data-testid="kpi-pie-hours-therapist">
            <CardContent className="p-5">
              <div className="flex items-end justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.hours_by_therapist")}</h2>
                <span className="caption-label text-stone-500">{compareLabel}</span>
              </div>
              {hoursByTherapist.length === 0 ? (
                <div className="py-8 text-center text-stone-500 text-sm">{t("reports.no_data")}</div>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer>
                    <PieChart margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
                      <Pie
                        data={hoursByTherapist}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius="65%"
                        label={renderPieLabel}
                        labelLine={false}
                      >
                        {hoursByTherapist.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })} h`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-stone-200/80" data-testid="kpi-pie-commission-therapist">
            <CardContent className="p-5">
              <div className="flex items-end justify-between mb-3">
                <h2 className="font-serif text-lg">{t("kpi.commission_by_therapist")}</h2>
                <span className="caption-label text-stone-500">{compareLabel}</span>
              </div>
              {payByTherapist.length === 0 ? (
                <div className="py-8 text-center text-stone-500 text-sm">{t("reports.no_data")}</div>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer>
                    <PieChart margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
                      <Pie
                        data={payByTherapist}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius="65%"
                        label={renderPieLabel}
                        labelLine={false}
                      >
                        {payByTherapist.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `฿${Number(v).toLocaleString("en-US")}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* All services sold in the window — sorted by revenue, labelled with ฿ */}
        <Card className="border-stone-200/80" data-testid="kpi-all-services">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-emerald-800" />
              <h2 className="font-serif text-lg">{t("kpi.services_sold")}</h2>
            </div>
            {allServices.length === 0 ? (
              <div className="py-8 text-center text-stone-500 text-sm">{t("reports.no_data")}</div>
            ) : (
              <div style={{ height: Math.max(240, allServices.length * 36 + 40) }}>
                <ResponsiveContainer>
                  <BarChart data={allServices} layout="vertical" margin={{ top: 8, right: 80, left: 40, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#78716c" tickFormatter={(v) => `฿${Math.round(v / 1000)}k`} />
                    <YAxis type="category" dataKey="display_name" tick={{ fontSize: 12 }} stroke="#44403c" width={200} />
                    <Tooltip
                      formatter={(v, name) => name === "customers" ? v : `฿${Math.round(v).toLocaleString("en-US")}`}
                    />
                    <Bar dataKey="revenue" name={t("kpi.revenue")} fill="#065f46" radius={[0, 3, 3, 0]}>
                      <LabelList
                        dataKey="revenue"
                        position="right"
                        formatter={(v) => `฿${Math.round(v).toLocaleString("en-US")}`}
                        style={{ fontSize: 11, fill: "#44403c" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
